import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Try hash first, then search params (iOS sometimes drops hash)
        let sessionId = null;
        const hash = window.location.hash;
        if (hash) {
          const params = new URLSearchParams(hash.substring(1));
          sessionId = params.get("session_id");
        }
        if (!sessionId) {
          const searchParams = new URLSearchParams(window.location.search);
          sessionId = searchParams.get("session_id");
        }

        if (!sessionId) {
          toast.error("Authentication failed — no session received");
          navigate("/", { replace: true });
          return;
        }

        // Retry up to 2 times for flaky connections
        let response = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            response = await fetch(`${API}/auth/session`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ session_id: sessionId }),
            });
            if (response.ok) break;
          } catch (e) {
            if (attempt === 1) throw e;
            await new Promise(r => setTimeout(r, 1000));
          }
        }

        if (!response || !response.ok) throw new Error("Failed to authenticate");

        const data = await response.json();
        login(data.user, data.session_token);
        toast.success(`Welcome, ${data.user.name}!`);
        navigate("/app", { replace: true, state: { user: data.user } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-[#4285f4] border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
